import React, {useEffect} from 'react';
import {
  AccessibilityInfo,
  Platform,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import {Icon, type IconName} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme, withAlpha} from '@/shared/ui/theme';

/**
 * An inline message that stays: a rejected password, a code that was sent, a warning
 * about what a destructive button is about to do.
 *
 * It is deliberately not a toast. A toast that fades after three seconds is exactly
 * the wrong affordance for the one message the user needs while re-reading what they
 * typed — the login screen has always made that argument in a comment, and this is it
 * made reusable, with a tone wash and a glyph so the message is classifiable before it
 * is read.
 *
 * The text takes the INK of its tone, never the fill: a banner is a sentence and a fill
 * colour is tuned to be a dot. A warning label on a 12% warning wash measures 2.65:1,
 * which is the exact trap the fill/ink split exists to close — see `status.tsx`.
 *
 * Это `Notice` ui-kit: мягкая заливка тона без рамки, значок и текст цветом тона.
 * `info` — синим, а не акцентом: акцент красный, и справка, набранная им, читалась
 * бы как тревога.
 */
export type BannerTone = 'error' | 'success' | 'warning' | 'info';

const styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing(3),
    paddingVertical: spacing(2) + 2,
    paddingHorizontal: spacing(3),
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
  },
  glyph: {paddingTop: 1},
  body: {flex: 1, gap: 2},
});

export function Banner({
  tone,
  title,
  message,
  icon,
  style,
  testID,
}: {
  tone: BannerTone;
  /** The headline. With no `message`, this is the whole banner. */
  title: string;
  message?: string;
  icon?: IconName;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();

  /*
    Результат действия должен быть СКАЗАН, а не только нарисован.

    `role="alert"` ниже — это веб, и там его достаточно. У Android свой механизм
    (`accessibilityLiveRegion`), а у iOS нет ни того, ни другого: VoiceOver узнаёт о
    появившемся тексте, только если ему об этом объявить. Отсюда три ветки на одно
    обещание — иначе «Пароль изменён» для незрячего просто не происходит.
  */
  useEffect(() => {
    if (Platform.OS === 'ios') {
      AccessibilityInfo.announceForAccessibility(message ? `${title}. ${message}` : title);
    }
  }, [title, message]);

  const skin =
    tone === 'error'
      ? {
          fill: p.destructive,
          fg: p.destructiveInk,
          bg: p.destructiveSoft,
          glyph: 'warning' as IconName,
        }
      : tone === 'success'
      ? {fill: p.signal, fg: p.signalInk, bg: p.signalSoft, glyph: 'check' as IconName}
      : tone === 'warning'
      ? {fill: p.warning, fg: p.warningInk, bg: p.warningSoft, glyph: 'warning' as IconName}
      : {fill: p.info, fg: p.infoInk, bg: p.infoSoft, glyph: 'mail' as IconName};

  return (
    <View
      testID={testID}
      accessibilityRole="alert"
      accessibilityLiveRegion={tone === 'error' ? 'assertive' : 'polite'}
      style={[
        styles.root,
        {backgroundColor: skin.bg, borderColor: withAlpha(skin.fill, 0.35)},
        style,
      ]}>
      <View style={styles.glyph}>
        <Icon name={icon ?? skin.glyph} size={16} color={skin.fill} />
      </View>
      <View style={styles.body}>
        <AppText variant="subtitle" color={skin.fg} style={{fontSize: 12, lineHeight: 16}}>
          {title}
        </AppText>
        {message ? (
          <AppText variant="caption" color={skin.fg} style={{opacity: 0.92}}>
            {message}
          </AppText>
        ) : null}
      </View>
    </View>
  );
}
